import React from "react";
import { useTranslation } from "react-i18next";

function ChatGPTSkill() {
    const { t } = useTranslation();

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-semibold mb-2">
                    {t("chatGPTSkill.title")}
                </h1>
                <p className="text-gray-600 ">
                    {t("chatGPTSkill.subtitle")}
                </p>

                <div className="mt-6">
                    <p className="text-gray-600 ">
                        {t("chatGPTSkill.intro")}
                    </p>
                </div>
            </div>

            <div className="space-y-12">
                {/* Key Capabilities Section */}
                <section>
                    <h2 className="text-2xl font-semibold mb-6">
                        {t("chatGPTSkill.capabilities.title")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-50  p-6 rounded-lg border border-blue-100 ">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">💬</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.capabilities.conversational.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.capabilities.conversational.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-green-50  p-6 rounded-lg border border-green-100 ">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">📊</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.capabilities.extraction.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.capabilities.extraction.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-purple-50  p-6 rounded-lg border border-purple-100 ">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">✍️</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.capabilities.generation.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.capabilities.generation.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-amber-50  p-6 rounded-lg border border-amber-100 ">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">🔄</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.capabilities.transformation.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.capabilities.transformation.description"
                                )}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Extraction Features Section */}
                <section className="bg-gray-50  p-8 rounded-lg">
                    <h2 className="text-2xl font-semibold mb-2">
                        {t("chatGPTSkill.extractionFeatures.title")}
                    </h2>
                    <p className="text-lg mb-6 text-purple-700  font-medium">
                        {t("chatGPTSkill.extractionFeatures.subtitle")}
                    </p>
                    <p className="mb-8">
                        {t("chatGPTSkill.extractionFeatures.intro")}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white  p-5 rounded-lg shadow-sm">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">📧</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.extractionFeatures.emails.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.extractionFeatures.emails.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-white  p-5 rounded-lg shadow-sm">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">📱</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.extractionFeatures.messages.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.extractionFeatures.messages.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-white  p-5 rounded-lg shadow-sm">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">📄</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.extractionFeatures.documents.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.extractionFeatures.documents.description"
                                )}
                            </p>
                        </div>
                        <div className="bg-white  p-5 rounded-lg shadow-sm">
                            <div className="flex items-center mb-3">
                                <span className="text-2xl mr-3">🌐</span>
                                <h3 className="text-lg font-medium">
                                    {t(
                                        "chatGPTSkill.extractionFeatures.websites.title"
                                    )}
                                </h3>
                            </div>
                            <p>
                                {t(
                                    "chatGPTSkill.extractionFeatures.websites.description"
                                )}
                            </p>
                        </div>
                    </div>
                </section>

                {/* How to Use Section */}
                <section>
                    <h2 className="text-2xl font-semibold mb-6">
                        {t("chatGPTSkill.howToUse.title")}
                    </h2>

                    {/* General Usage */}
                    <div className="mb-8">
                        <h3 className="text-xl font-medium mb-4">
                            {t("chatGPTSkill.howToUse.general.title")}
                        </h3>
                        <ol className="list-decimal pl-6 space-y-2 mb-4">
                            <li>{t("chatGPTSkill.howToUse.general.step1")}</li>
                            <li>{t("chatGPTSkill.howToUse.general.step2")}</li>
                            <li>{t("chatGPTSkill.howToUse.general.step3")}</li>
                        </ol>
                    </div>

                    {/* Data Extraction Example */}
                    <div className="mb-8">
                        <h3 className="text-xl font-medium mb-4">
                            {t("chatGPTSkill.howToUse.extraction.title")}
                        </h3>
                        <p className="mb-4">
                            {t("chatGPTSkill.howToUse.extraction.description")}
                        </p>

                        <div className="bg-gray-50  border border-gray-200  rounded-lg overflow-hidden">
                            <div className="bg-blue-100  p-4">
                                <p className="font-medium">
                                    {t(
                                        "chatGPTSkill.howToUse.extraction.example.prompt"
                                    )}
                                </p>
                            </div>
                            <div className="p-4 bg-gray-100  border-t border-b border-gray-200 ">
                                <p className="italic">
                                    {t(
                                        "chatGPTSkill.howToUse.extraction.example.message"
                                    )}
                                </p>
                            </div>
                            <div className="p-4 font-mono text-sm whitespace-pre-line">
                                {t(
                                    "chatGPTSkill.howToUse.extraction.example.response"
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Generation Example */}
                    <div>
                        <h3 className="text-xl font-medium mb-4">
                            {t("chatGPTSkill.howToUse.generation.title")}
                        </h3>
                        <p className="mb-4">
                            {t("chatGPTSkill.howToUse.generation.description")}
                        </p>

                        <div className="bg-gray-50  border border-gray-200  rounded-lg overflow-hidden">
                            <div className="bg-green-100 dark:bg-green-900 p-4">
                                <p className="font-medium">
                                    {t(
                                        "chatGPTSkill.howToUse.generation.example.prompt"
                                    )}
                                </p>
                            </div>
                            <div className="p-4 font-mono text-sm whitespace-pre-line">
                                {t(
                                    "chatGPTSkill.howToUse.generation.example.response"
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Integration with Automation Section */}
                <section className="bg-blue-50  p-8 rounded-lg">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t("chatGPTSkill.integrationWithAutomation.title")}
                    </h2>
                    <p className="mb-6">
                        {t(
                            "chatGPTSkill.integrationWithAutomation.description"
                        )}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                        <div className="bg-white  p-6 rounded-lg shadow-sm">
                            <h3 className="text-lg font-medium mb-4">
                                {t(
                                    "chatGPTSkill.integrationWithAutomation.example1.title"
                                )}
                            </h3>
                            <ol className="list-decimal pl-6 space-y-3">
                                {t(
                                    "chatGPTSkill.integrationWithAutomation.example1.steps",
                                    { returnObjects: true }
                                ).map((step, index) => (
                                    <li key={index}>{step}</li>
                                ))}
                            </ol>
                        </div>
                        <div className="bg-white  p-6 rounded-lg shadow-sm">
                            <h3 className="text-lg font-medium mb-4">
                                {t(
                                    "chatGPTSkill.integrationWithAutomation.example2.title"
                                )}
                            </h3>
                            <ol className="list-decimal pl-6 space-y-3">
                                {t(
                                    "chatGPTSkill.integrationWithAutomation.example2.steps",
                                    { returnObjects: true }
                                ).map((step, index) => (
                                    <li key={index}>{step}</li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border-l-4 border-yellow-500">
                        <p className="font-medium">
                            {t(
                                "chatGPTSkill.integrationWithAutomation.variablesUsage"
                            )}
                        </p>
                    </div>
                </section>

                {/* Best Practices Section */}
                <section>
                    <h2 className="text-2xl font-semibold mb-4">
                        {t("chatGPTSkill.bestPractices.title")}
                    </h2>
                    <ul className="space-y-4">
                        <li className="flex">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>
                                <strong>
                                    {t("chatGPTSkill.bestPractices.practice1")}
                                </strong>
                            </span>
                        </li>
                        <li className="flex">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>
                                <strong>
                                    {t("chatGPTSkill.bestPractices.practice2")}
                                </strong>
                            </span>
                        </li>
                        <li className="flex">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>
                                <strong>
                                    {t("chatGPTSkill.bestPractices.practice3")}
                                </strong>
                            </span>
                        </li>
                        <li className="flex">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>
                                <strong>
                                    {t("chatGPTSkill.bestPractices.practice4")}
                                </strong>
                            </span>
                        </li>
                        <li className="flex">
                            <span className="text-green-600 mr-2">✓</span>
                            <span>
                                <strong>
                                    {t("chatGPTSkill.bestPractices.practice5")}
                                </strong>
                            </span>
                        </li>
                    </ul>
                </section>

                {/* Limitations Section */}
                <section className="bg-gray-50  p-6 rounded-lg">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t("chatGPTSkill.limitations.title")}
                    </h2>
                    <ul className="space-y-3">
                        <li className="flex items-start">
                            <span className="text-amber-500 mr-2 mt-0.5">
                                ⚠️
                            </span>
                            <span>{t("chatGPTSkill.limitations.limit1")}</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-amber-500 mr-2 mt-0.5">
                                ⚠️
                            </span>
                            <span>{t("chatGPTSkill.limitations.limit2")}</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-amber-500 mr-2 mt-0.5">
                                ⚠️
                            </span>
                            <span>{t("chatGPTSkill.limitations.limit3")}</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-amber-500 mr-2 mt-0.5">
                                ⚠️
                            </span>
                            <span>{t("chatGPTSkill.limitations.limit4")}</span>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    );
}

export default ChatGPTSkill;
